/**
 * Scheduling input for admin broadcasts. Admins think in India time
 * (IST, fixed +05:30, no daylight saving), so the page sends a date +
 * time that we pin to that zone before storing a UTC moment.
 */

const MAX_DAYS_AHEAD = 30;
const DAY_MS = 86_400_000;

/** "2026-10-08" + "14:00" (IST) → ISO UTC string, or null if malformed. */
export function istWallClockToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
    return null;
  }
  const d = new Date(`${date}T${time}:00+05:30`);
  if (Number.isNaN(d.getTime())) return null;
  // Reject rollovers like Feb 31 → Mar 3.
  const back = new Date(d.getTime() + 330 * 60_000).toISOString();
  if (back.slice(0, 10) !== date || back.slice(11, 16) !== time) return null;
  return d.toISOString();
}

export type SendAtResult =
  | { ok: true; sendAt: Date | null }
  | { ok: false; error: string };

/** Validates the request's optional sendAt. null = send now. */
export function parseSendAt(raw: unknown, now: Date): SendAtResult {
  if (raw === undefined || raw === null || raw === "") {
    return { ok: true, sendAt: null };
  }
  if (typeof raw !== "string") {
    return { ok: false, error: "Invalid scheduled time" };
  }
  const sendAt = new Date(raw);
  if (Number.isNaN(sendAt.getTime())) {
    return { ok: false, error: "Invalid scheduled time" };
  }
  if (sendAt.getTime() <= now.getTime()) {
    return { ok: false, error: "Scheduled time must be in the future" };
  }
  if (sendAt.getTime() - now.getTime() > MAX_DAYS_AHEAD * DAY_MS) {
    return { ok: false, error: `Schedule at most ${MAX_DAYS_AHEAD} days ahead` };
  }
  return { ok: true, sendAt };
}
